import React from 'react';
import { Box, Typography } from '@mui/material';
import { ShoppingBag, ChevronRight } from 'lucide-react';
import { TOKENS, COLOR_POR_ESTADO } from '../state/colors';
import { fs } from '../state/typography';
import { useIconScale } from '../state/useIconScale';
import { formatMonedaCompacta } from '../state/format';
import { EstadoBolsa, ESTADOS_LABEL } from '../state/bolsaMachine';
import { BolsaCentro } from '../types';

interface PipelineFlowProps {
  bolsasAbiertas: BolsaCentro[];
  cotizaciones: BolsaCentro[];
  onFiltrar: (estado: EstadoBolsa | 'TODAS') => void;
  onVerBolsasAbiertas: () => void;
}

const ETAPAS: EstadoBolsa[] = [EstadoBolsa.BORRADOR, EstadoBolsa.ENVIADA];
const TERMINALES: EstadoBolsa[] = [EstadoBolsa.GANADA, EstadoBolsa.PERDIDA, EstadoBolsa.VENCIDA, EstadoBolsa.ANULADA];

function totalPorEstado(cotizaciones: BolsaCentro[], estado: EstadoBolsa) {
  return cotizaciones.filter((b) => b.estado_codigo === estado).reduce((acc, b) => acc + b.total, 0);
}

function countPorEstado(cotizaciones: BolsaCentro[], estado: EstadoBolsa) {
  return cotizaciones.filter((b) => b.estado_codigo === estado).length;
}

export const PipelineFlow: React.FC<PipelineFlowProps> = ({ bolsasAbiertas, cotizaciones, onFiltrar, onVerBolsasAbiertas }) => {
  const shoppingBagSize = useIconScale(14);
  const chevronRightSize = useIconScale(17);
  const totalBolsas = bolsasAbiertas.reduce((acc, b) => acc + b.total, 0);
  const maxEtapa = Math.max(...ETAPAS.map((e) => totalPorEstado(cotizaciones, e)), 1);

  return (
    <Box>
      {/* SPEC-105 (CH-01) — título "Flujo del pipeline" ahora lo provee el SectionCard que
          envuelve este componente en CotizacionesTab/ResumenTab; se elimina el duplicado. */}
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '10px', overflowX: 'auto', paddingBottom: '4px' }}>
        {/* Nodo Bolsas */}
        <Box
          data-testid="flow-node-bolsas"
          onClick={onVerBolsasAbiertas}
          sx={{
            cursor: 'pointer',
            minWidth: 150,
            border: `1.5px dashed ${TOKENS.faint}`,
            backgroundColor: TOKENS.wash,
            borderRadius: '16px',
            padding: '14px 16px',
            transition: 'all .15s ease',
            '&:hover': { transform: 'translateY(-3px)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: TOKENS.gray }} />
            <ShoppingBag size={shoppingBagSize} color={TOKENS.gray} />
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), fontWeight: 600, color: TOKENS.text }}>
              Bolsas · carrito
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(30), fontWeight: 300, letterSpacing: '-1px', color: TOKENS.ink }}>
            {bolsasAbiertas.length}
          </Typography>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), color: TOKENS.muted }}>
            ~{formatMonedaCompacta(totalBolsas)} estimado
          </Typography>
          <Box sx={{ height: 5, borderRadius: '999px', backgroundColor: TOKENS.faint, opacity: 0.3, marginTop: '8px' }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(9.5), fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: TOKENS.muted }}>
            CONVERTIR ›
          </Typography>
        </Box>

        {ETAPAS.map((estado, idx) => {
          const total = totalPorEstado(cotizaciones, estado);
          const count = countPorEstado(cotizaciones, estado);
          const ancho = Math.max((total / maxEtapa) * 100, total > 0 ? 8 : 0);
          return (
            <React.Fragment key={estado}>
              <Box
                data-testid={`flow-stage-${estado}`}
                onClick={() => onFiltrar(estado)}
                sx={{
                  cursor: 'pointer',
                  minWidth: 150,
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  padding: '14px 16px',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,.65), 0 2px 4px rgba(46,36,48,.06), 0 10px 24px rgba(46,36,48,.10)',
                  transition: 'all .15s ease',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 4px 8px rgba(46,36,48,.09), 0 16px 34px rgba(46,36,48,.17)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: COLOR_POR_ESTADO[estado] }} />
                  <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), fontWeight: 600, color: TOKENS.text }}>
                    {ESTADOS_LABEL[estado]}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(30), fontWeight: 300, letterSpacing: '-1px', color: TOKENS.ink }}>
                  {count}
                </Typography>
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), color: TOKENS.muted }}>
                  {formatMonedaCompacta(total)}
                </Typography>
                <Box sx={{ height: 5, borderRadius: '999px', backgroundColor: TOKENS.wash, marginTop: '8px', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${ancho}%`, borderRadius: '999px', backgroundColor: COLOR_POR_ESTADO[estado], transition: 'width .5s' }} />
                </Box>
              </Box>
              {idx < ETAPAS.length - 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ChevronRight size={chevronRightSize} color={TOKENS.faint} />
                </Box>
              )}
            </React.Fragment>
          );
        })}

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRight size={chevronRightSize} color={TOKENS.faint} />
        </Box>

        {/* Resultados terminales */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(90px, 1fr))', gap: '8px', flex: 1 }}>
          {TERMINALES.map((estado) => {
            const total = totalPorEstado(cotizaciones, estado);
            const count = countPorEstado(cotizaciones, estado);
            return (
              <Box
                key={estado}
                data-testid={`flow-outcome-${estado}`}
                onClick={() => onFiltrar(estado)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: TOKENS.wash,
                  borderRadius: '14px',
                  padding: '12px 10px',
                  textAlign: 'center',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,.6), 0 2px 4px rgba(46,36,48,.05)',
                  transition: 'all .15s ease',
                  '&:hover': { backgroundColor: '#fff', transform: 'translateY(-2px)', boxShadow: '0 4px 8px rgba(46,36,48,.08), 0 14px 26px rgba(46,36,48,.13)' },
                }}
              >
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(23), fontWeight: 700, color: TOKENS.ink }}>
                  {count}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: COLOR_POR_ESTADO[estado] }} />
                  <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), fontWeight: 600, color: TOKENS.text }}>
                    {ESTADOS_LABEL[estado]}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted }}>
                  {formatMonedaCompacta(total)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
